import { isDefined } from '@mfw/common';

import type {
  UiNutritionStatistics,
  UiNutritionMealSlot,
  UiNutritionDay,
} from '../../schemas/nutritionStats.schema';
import type {
  FetchNutritionStatisticsArgs,
  ApiTimeseries,
} from '../types/stats.types';

import { NUTRITION_MEAL_SLOTS } from '../../schemas/nutritionStats.schema';
import { civilDaysOf } from './sleepStats.mappers';

export const mealMetricKey = (slot: (typeof NUTRITION_MEAL_SLOTS)[number]) =>
  `kcal_intake_${slot}`;

/**
 * Daily series the screen is built from: the five meal fields `kcal_intake`
 * sums, plus water. `kcal_intake` itself is not read — its legacy food-diary
 * rows (before the five fields existed) carry no meals to count.
 */
export const NUTRITION_DAILY_KEYS = [
  ...NUTRITION_MEAL_SLOTS.map(mealMetricKey),
  'water_intake',
];

function dailyValues(timeseries: ApiTimeseries | null, metricKey: string) {
  const series = timeseries?.series.find((s) => s.metricKey === metricKey);
  return new Map(
    (series?.points ?? [])
      .filter((point) => isDefined(point.value))
      .map((point) => [point.bucket, point.value as number]),
  );
}

const divide = (sum: number, count: number) => (count ? sum / count : null);

export function mapNutritionStatisticsFromApi({
  args,
  timeseries,
}: {
  args: FetchNutritionStatisticsArgs;
  timeseries: ApiTimeseries | null;
}): UiNutritionStatistics {
  const days = civilDaysOf(args.range).filter((day) => day <= args.today);
  const loggedDays = new Set<string>();
  let totalKcal = 0;
  let meals = 0;

  const slotValues = NUTRITION_MEAL_SLOTS.map((key) =>
    dailyValues(timeseries, mealMetricKey(key)),
  );

  const slotTotals = NUTRITION_MEAL_SLOTS.map((key, index) => {
    const values = slotValues[index];
    let slotKcal = 0;
    let daysEaten = 0;
    for (const day of days) {
      const kcal = values.get(day);
      if (!isDefined(kcal)) continue;
      loggedDays.add(day);
      if (kcal > 0) {
        slotKcal += kcal;
        daysEaten += 1;
      }
    }
    totalKcal += slotKcal;
    meals += daysEaten;
    return { daysEaten, key, totalKcal: slotKcal };
  });

  const slots: UiNutritionMealSlot[] = slotTotals.map((slot) => ({
    ...slot,
    avgKcal: divide(slot.totalKcal, slot.daysEaten),
    sharePct: totalKcal ? (slot.totalKcal / totalKcal) * 100 : null,
  }));

  const water = dailyValues(timeseries, 'water_intake');
  const waterDays = days.filter((day) => water.has(day));
  const waterSum = waterDays.reduce((sum, day) => sum + water.get(day)!, 0);

  const dayRows: UiNutritionDay[] = days.map((date) => {
    const slotKcal = slotValues.map((values) => values.get(date) ?? null);
    const filled = slotKcal.filter(isDefined);
    return {
      date,
      kcal: filled.length ? filled.reduce((sum, kcal) => sum + kcal, 0) : null,
      meals: filled.filter((kcal) => kcal > 0).length,
      slotKcal,
      waterL: water.get(date) ?? null,
    };
  });

  return {
    avgKcalPerDay: divide(totalKcal, loggedDays.size),
    days: dayRows,
    daysInPeriod: days.length,
    kcalPerMeal: divide(totalKcal, meals),
    loggedDays: loggedDays.size,
    meals,
    mealsPerDay: divide(meals, loggedDays.size),
    slots,
    totalKcal: loggedDays.size ? totalKcal : null,
    water: { avgL: divide(waterSum, waterDays.length), days: waterDays.length },
  };
}
